import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useMapStore } from '@/stores/mapStore';
import { projectOnPolyline } from '@/utils/routeUtils';
import type { TripRouteResponse } from '@/types/route';

const LEVEL_COLORS: Record<string, string> = {
  critical: '#ef4444',
  alert: '#f59e0b',
  warning: '#eab308',
  info: '#3b82f6',
};

const TRUCK_ICON = L.divIcon({
  className: '',
  html: `<div style="
    background:#1d4ed8; width:32px; height:32px;
    border-radius:50%; border:3px solid white;
    box-shadow:0 3px 10px rgba(0,0,0,0.5);
    display:flex; align-items:center; justify-content:center;
    font-size:16px; line-height:1;
  "><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 13.52 9H12"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// SVG icons (module-level to avoid recreating)
const casetaSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="14" height="14"><path d="M2 20h2V8h2V6H4V4h4v2h2v2h2V6h4V4h4v2h-2v2h2v12h2v2H2v-2zm6 0h2v-6h4v6h2V8h-2V6h-4v2H8v12z"/><circle cx="14" cy="7" r="1.2" fill="white"/></svg>`;
const gasSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="14" height="14"><path d="M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5z"/></svg>`;

export default function RouteMapLayer() {
  const map = useMap();
  const traveledLineRef = useRef<L.Polyline | null>(null);
  const aheadLineRef = useRef<L.Polyline | null>(null);
  const destMarkerRef = useRef<L.Marker | null>(null);
  const poisLayerRef = useRef<L.LayerGroup | null>(null);
  const truckMarkerRef = useRef<L.Marker | null>(null);
  const drawnAliasRef = useRef<string | null>(null);
  const lastProgressKmRef = useRef<number | null>(null);

  // Draw POI markers filtered by truck progress
  function drawPOIs(
    routeData: TripRouteResponse,
    progressKm: number | null,
  ) {
    // Clean previous POI layer
    if (poisLayerRef.current) {
      map.removeLayer(poisLayerRef.current);
      poisLayerRef.current = null;
    }

    const poisGroup = L.layerGroup().addTo(map);
    poisLayerRef.current = poisGroup;

    const polyline = routeData.polyline;
    if (!polyline || polyline.length < 2) return;

    // Filter casetas
    const casetas = progressKm != null
      ? (routeData.casetas ?? []).filter(c => (c.distance_from_origin_km ?? 0) > progressKm)
      : (routeData.casetas ?? []);

    casetas.forEach((c) => {
      const marker = L.marker([c.lat, c.lon], {
        icon: L.divIcon({
          className: '',
          html: `<div style="
            background:#10b981; width:24px; height:24px;
            border-radius:50%; border:2px solid white;
            box-shadow:0 2px 8px rgba(0,0,0,0.35);
            display:flex; align-items:center; justify-content:center;
          ">${casetaSvg}</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      });
      marker.bindTooltip(c.name, { permanent: false, direction: 'top' });
      poisGroup.addLayer(marker);
    });

    // Filter gasolineras
    const gasolineras = progressKm != null
      ? (routeData.gasolineras ?? []).filter(g => (g.distance_from_origin_km ?? 0) > progressKm)
      : (routeData.gasolineras ?? []);

    gasolineras.forEach((g) => {
      const marker = L.marker([g.lat, g.lon], {
        icon: L.divIcon({
          className: '',
          html: `<div style="
            background:#f59e0b; width:24px; height:24px;
            border-radius:50%; border:2px solid white;
            box-shadow:0 2px 8px rgba(0,0,0,0.35);
            display:flex; align-items:center; justify-content:center;
          ">${gasSvg}</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      });
      marker.bindTooltip(g.name, { permanent: false, direction: 'top' });
      poisGroup.addLayer(marker);
    });

    // Filter risk alerts
    const allRiskAlerts = routeData.risk_alerts ?? [];
    const riskAlerts = progressKm != null
      ? allRiskAlerts.filter(a => (a.km ?? 0) > progressKm)
      : allRiskAlerts;

    const seen = new Set<string>();
    riskAlerts.forEach((a) => {
      let lat = a.lat;
      let lon = a.lon;

      // Interpolate position if only km is provided
      if ((lat == null || lon == null) && a.km != null && polyline.length > 1) {
        const polyKm = [0];
        for (let i = 1; i < polyline.length; i++) {
          const [lat1, lon1] = polyline[i - 1];
          const [lat2, lon2] = polyline[i];
          const R = 6371;
          const dLat = ((lat2 - lat1) * Math.PI) / 180;
          const dLon = ((lon2 - lon1) * Math.PI) / 180;
          const x =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
              Math.cos((lat2 * Math.PI) / 180) *
              Math.sin(dLon / 2) ** 2;
          polyKm.push(polyKm[i - 1] + 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
        }
        for (let i = 1; i < polyKm.length; i++) {
          if (polyKm[i] >= a.km!) {
            lat = polyline[i][0];
            lon = polyline[i][1];
            break;
          }
        }
      }

      if (lat == null || lon == null) return;

      const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;
      if (seen.has(key)) return;
      seen.add(key);

      const color = LEVEL_COLORS[a.level] || '#3b82f6';
      const marker = L.marker([lat, lon], {
        icon: L.divIcon({
          className: '',
          html: `<div style="
            background:${color}; width:20px; height:20px;
            border-radius:4px; border:2px solid white;
            box-shadow:0 2px 6px rgba(0,0,0,0.4);
            display:flex; align-items:center; justify-content:center;
            font-size:10px; color:white;
          ">⚠</div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      });
      marker.bindTooltip(`⚠️ ${a.zone_name || 'Zona de riesgo'}`, {
        permanent: false,
        direction: 'top',
      });
      marker.bindPopup(
        `<div style="font-family:Inter,sans-serif; min-width:180px;">
          <div style="font-weight:700; color:${color}; margin-bottom:4px;">
            ⚠ ${a.zone_name || 'Alerta'}
          </div>
          <div style="font-size:12px;">${a.message || ''}</div>
          ${a.km != null ? `<div style="font-size:11px; margin-top:4px; opacity:0.7;">${a.km} km</div>` : ''}
        </div>`
      );
      poisGroup.addLayer(marker);
    });
  }

  // Split polyline into traveled (faded) and ahead (bright) based on nearestIndex
  function drawSplitPolyline(
    polyline: [number, number][],
    nearestIndex: number | null,
  ) {
    // Remove old lines
    if (traveledLineRef.current) { map.removeLayer(traveledLineRef.current); traveledLineRef.current = null; }
    if (aheadLineRef.current) { map.removeLayer(aheadLineRef.current); aheadLineRef.current = null; }

    const coords = polyline.map((p) => [p[0], p[1]] as L.LatLngTuple);

    if (nearestIndex == null || nearestIndex <= 0) {
      // No progress info — draw full line bright
      aheadLineRef.current = L.polyline(coords, { color: '#3b82f6', weight: 5, opacity: 0.8 }).addTo(map);
      return;
    }

    // Split at nearestIndex+1 (include the split point in both segments for continuity)
    const splitIdx = Math.min(nearestIndex + 1, coords.length - 1);
    const traveledCoords = coords.slice(0, splitIdx + 1);
    const aheadCoords = coords.slice(splitIdx);

    if (traveledCoords.length >= 2) {
      traveledLineRef.current = L.polyline(traveledCoords, {
        color: '#94a3b8', weight: 4, opacity: 0.35, dashArray: '8 6',
      }).addTo(map);
    }
    if (aheadCoords.length >= 2) {
      aheadLineRef.current = L.polyline(aheadCoords, {
        color: '#3b82f6', weight: 5, opacity: 0.8,
      }).addTo(map);
    } else {
      // Very end — just draw all faded
      aheadLineRef.current = null;
    }
  }

  function cleanup() {
    if (traveledLineRef.current) { map.removeLayer(traveledLineRef.current); traveledLineRef.current = null; }
    if (aheadLineRef.current) { map.removeLayer(aheadLineRef.current); aheadLineRef.current = null; }
    if (destMarkerRef.current) {
      map.removeLayer(destMarkerRef.current);
      destMarkerRef.current = null;
    }
    if (poisLayerRef.current) {
      map.removeLayer(poisLayerRef.current);
      poisLayerRef.current = null;
    }
    // No remover truck marker aquí — lo gestiona la suscripción de livePosition.
    // cleanup() se llama al redibujar ruta, y el useEffect corre DESPUÉS
    // de la suscripción, lo que causaba que el truck se borrara justo después
    // de crearse.
  }

  function drawDestinationMarker(lat: number, lon: number, name?: string | null) {
    if (destMarkerRef.current) {
      map.removeLayer(destMarkerRef.current);
      destMarkerRef.current = null;
    }

    const destMarker = L.marker([lat, lon], {
      icon: L.divIcon({
        className: '',
        html: `<div style="
          background:#ef4444; width:28px; height:28px;
          border-radius:50% 50% 50% 0; border:3px solid white;
          box-shadow:0 3px 8px rgba(0,0,0,0.4);
          transform:rotate(-45deg);
          display:flex; align-items:center; justify-content:center;
        "><span style="transform:rotate(45deg); font-size:11px; color:white;">🏁</span></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      }),
    }).addTo(map);
    destMarker.bindTooltip(name || 'Destino', {
      permanent: false,
      direction: 'top',
      offset: [0, -20],
    });
    destMarkerRef.current = destMarker;
  }

  function drawDestinationOnly(routeData: TripRouteResponse, isNewRoute: boolean) {
    const destLat = routeData.destination_lat;
    const destLon = routeData.destination_lon;
    if (!isFiniteNumber(destLat) || !isFiniteNumber(destLon)) return false;

    drawDestinationMarker(destLat, destLon, routeData.destination_name);
    drawnAliasRef.current = routeData.alias ?? '';

    if (isNewRoute) {
      const bounds = L.latLngBounds([[destLat, destLon]]);
      const livePos = useMapStore.getState().livePosition;
      if (isFiniteNumber(livePos?.lat) && isFiniteNumber(livePos?.lon)) {
        bounds.extend([livePos.lat, livePos.lon]);
      }
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 17 });
    }

    return true;
  }

  function drawRoute(routeData: TripRouteResponse) {
    const polyline = routeData.polyline;
    if (!polyline || polyline.length < 2) return;

    // Get initial projection for split line
    const livePos = useMapStore.getState().livePosition;
    const positionIsOffRoute = livePos?.off_route === true;
    let nearestIndex: number | null = null;
    let progressKm: number | null = positionIsOffRoute ? null : (livePos?.progress_km ?? null);

    if (!positionIsOffRoute && livePos?.lat != null && livePos?.lon != null) {
      const proj = projectOnPolyline(livePos.lat, livePos.lon, polyline as [number, number][]);
      if (proj) {
        nearestIndex = proj.nearestIndex;
        if (progressKm == null) progressKm = proj.totalKm * (proj.progressPercent / 100);
      }
    }

    // Draw split polyline (traveled faded + ahead bright)
    drawSplitPolyline(polyline as [number, number][], nearestIndex);

    const coords = polyline.map((p) => [p[0], p[1]] as L.LatLngTuple);
    const fallbackDestCoord = coords[coords.length - 1];
    let destLat = fallbackDestCoord[0];
    let destLon = fallbackDestCoord[1];
    if (isFiniteNumber(routeData.destination_lat) && isFiniteNumber(routeData.destination_lon)) {
      destLat = routeData.destination_lat;
      destLon = routeData.destination_lon;
    }
    drawDestinationMarker(destLat, destLon, routeData.destination_name);

    // Fit bounds ONLY on first draw for this route
    const isNewRoute = drawnAliasRef.current !== (routeData.alias ?? '');
    if (isNewRoute) {
      // Use the ahead line or build full bounds
      const allCoords = polyline.map((p) => [p[0], p[1]] as L.LatLngTuple);
      const boundsLine = L.polyline(allCoords);
      map.fitBounds(boundsLine.getBounds(), { padding: [50, 50] });
    }
    drawnAliasRef.current = routeData.alias ?? '';
    lastProgressKmRef.current = progressKm;
    drawPOIs(routeData, progressKm);
  }

  // Reactive route data from store
  const routeData = useMapStore((s) => s.routeData);

  // React to routeData changes: cleanup when null, draw when new route
  useEffect(() => {
    if (!routeData) {
      // Only cleanup if we had something drawn
      if (drawnAliasRef.current) {
        cleanup();
        try { map.closePopup(); } catch (_) { /* noop */ }
        drawnAliasRef.current = null;
        lastProgressKmRef.current = null;
      }
      return;
    }

    if (!routeData.polyline?.length) {
      const isNewRoute = drawnAliasRef.current !== (routeData.alias ?? '');
      cleanup();
      lastProgressKmRef.current = null;
      if (!drawDestinationOnly(routeData, isNewRoute)) {
        drawnAliasRef.current = null;
      }
      return;
    }

    // Same route already drawn — skip
    if (drawnAliasRef.current === routeData.alias && (aheadLineRef.current || traveledLineRef.current)) {
      return;
    }

    // New route — clean old layers and draw
    cleanup();
    drawRoute(routeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeData, map]);

  // Subscribe to livePosition — move truck marker + redraw filtered POIs
  useEffect(() => {
    const unsub = useMapStore.subscribe((state, prev) => {
      const pos = state.livePosition;

      // Truck marker logic
      if (!pos || pos.lat == null || pos.lon == null) {
        if (truckMarkerRef.current) {
          map.removeLayer(truckMarkerRef.current);
          truckMarkerRef.current = null;
        }
        return;
      }

      const latLng: L.LatLngExpression = [pos.lat, pos.lon];

      if (truckMarkerRef.current) {
        truckMarkerRef.current.setLatLng(latLng);
      } else {
        truckMarkerRef.current = L.marker(latLng, {
          icon: TRUCK_ICON,
          zIndexOffset: 1000,
        }).addTo(map);
      }

      const speedStr = pos.speed != null ? `${Math.round(pos.speed)} km/h` : '';
      truckMarkerRef.current.bindTooltip(
        `Posición actual${speedStr ? ' · ' + speedStr : ''}`,
        { permanent: false, direction: 'top', offset: [0, -16] }
      );

      // Calculate progressKm + nearestIndex: prefer server, fallback to client projection
      const positionIsOffRoute = pos.off_route === true;
      let newProgressKm = positionIsOffRoute ? null : (pos.progress_km ?? null);
      let nearestIndex: number | null = null;
      const routeData = useMapStore.getState().routeData;

      if (!positionIsOffRoute && pos.lat != null && pos.lon != null && routeData?.polyline?.length) {
        const proj = projectOnPolyline(pos.lat, pos.lon, routeData.polyline as [number, number][]);
        if (proj) {
          nearestIndex = proj.nearestIndex;
          if (newProgressKm == null) {
            newProgressKm = proj.totalKm * (proj.progressPercent / 100);
          }
        }
      }

      // Redraw POIs + update split polyline if progress changed
      if (newProgressKm !== lastProgressKmRef.current) {
        lastProgressKmRef.current = newProgressKm;
        if (routeData?.polyline?.length) {
          drawPOIs(routeData, newProgressKm);
          drawSplitPolyline(routeData.polyline as [number, number][], nearestIndex);
        }
      }
    });

    return () => {
      unsub();
      if (truckMarkerRef.current) {
        map.removeLayer(truckMarkerRef.current);
        truckMarkerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}
